import { CheckCircle, Shield, FileText, TrendingUp } from 'lucide-react';

const compliancePoints = [
  {
    icon: <FileText className="w-6 h-6" />,
    title: 'ISO 9001',
    description: 'Built-in audit trails and documentation meet ISO 9001 quality management requirements automatically.',
  },
  {
    icon: <Shield className="w-6 h-6" />,
    title: 'Enterprise Compliance',
    description: 'Every action logged, every decision traceable. No separate audit systems needed.',
  },
  {
    icon: <TrendingUp className="w-6 h-6" />,
    title: 'Regulatory Ready',
    description: 'SOC 2, HIPAA, GDPR-friendly architecture. Complete data lineage and control.',
  },
  {
    icon: <CheckCircle className="w-6 h-6" />,
    title: '100% Audit Trail',
    description: 'Full parent-child traceability from Kernels → Bots → Processes → Records.',
  },
];

export const ComplianceSection = () => {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 bg-slate-50 dark:bg-slate-900">
      <div className="text-center mb-16">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 rounded-full text-sm font-medium mb-4">
          <Shield className="w-4 h-4" />
          Enterprise Compliance
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
          Trust You Can Prove
        </h2>
        <p className="text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
          ~94% of agent deployments fail in production because nobody can see what the AI did. Raisy's answer is auditability by design: every action logged, every decision traceable, ISO 9001 aligned out of the box. The platform <em>is</em> your quality management system.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
        {compliancePoints.map((point, idx) => (
          <div
            key={idx}
            className="p-6 md:p-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:shadow-lg transition-shadow"
            data-aos="fade-up"
            data-aos-delay={idx * 100}
          >
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-100 to-teal-100 dark:from-cyan-900 dark:to-teal-900 flex items-center justify-center text-cyan-600 dark:text-cyan-400 mb-4">
              {point.icon}
            </div>
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              {point.title}
            </h3>
            <p className="text-slate-600 dark:text-slate-300">
              {point.description}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-16 p-8 md:p-10 bg-gradient-to-r from-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:to-teal-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800">
        <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
          What Makes Raisy Different
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
              <span className="text-cyan-600 dark:text-cyan-400">✓</span>
              Traditional Systems
            </h4>
            <p className="text-slate-700 dark:text-slate-300 text-sm">
              Compliance = separate audit system + manual documentation + expensive external consultants
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
              <span className="text-cyan-600 dark:text-cyan-400">✓</span>
              Raisy
            </h4>
            <p className="text-slate-700 dark:text-slate-300 text-sm">
              Compliance = automated, built-in, real-time audit trails that satisfy ISO 9001, SOC 2, and HIPAA requirements
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
